'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import { BottomSheet, Button } from '@miso/design-system';

/**
 * 방 추가 — 타입 선택 옵션 한 항목.
 *
 * 어댑터마다 사용하는 type 도메인이 다르다:
 *   - HouseStructureFlowAdapter (구버전 DoneScreen): 한국어 라벨이 곧 value
 *     (`'현관' | '방' | '거실' | ...` + 'other').
 *   - RoomEditFlowAdapter: API 의 `RoomTypeValue` 열거형
 *     (`'ENTRANCE' | 'LIVING_ROOM' | ...` + 'OTHER_SPACE').
 *
 * 두 도메인 모두를 그대로 받기 위해 `value: T extends string` 으로 제네릭화한다.
 */
export interface AddRoomTypeOption<T extends string> {
  value: T;
  label: string;
}

export interface AddRoomTypeSheetProps<T extends string> {
  open: boolean;
  options: ReadonlyArray<AddRoomTypeOption<T>>;
  selected: T | null;
  /** 진행 중 표시(disabled + 라벨 변경) */
  isSubmitting?: boolean;
  /** "추가하기" CTA 라벨. 미지정 시 '추가하기'. */
  ctaLabel?: string;
  /** 진행 중에 표시되는 CTA 라벨. 미지정 시 '추가 중...'. */
  ctaSubmittingLabel?: string;
  /** 시트 제목. 미지정 시 '어떤 방을 추가하시겠어요?'. */
  title?: string;
  onSelect: (value: T) => void;
  onConfirm: () => void;
  onClose: () => void;
  container?: HTMLElement | null;
}

/**
 * 방 추가 — 타입 선택 BottomSheet.
 *
 * 방 추가 워크플로우는 `house-structure-flow` (집 구조 입력 시 + 편집 시) 와
 * `room-observing-flow` (picker 의 "방 수정하기") 양쪽에서 거의 동일한 UI 로
 * 사용된다. 두 곳의 코드 중복을 제거하기 위해 본 컴포넌트로 분리.
 *
 * 호출 흐름:
 *   1. 사용자가 "+ 방 추가하기" 클릭 → `onClose` 가 트리거되는 시점까지 시트 표시
 *   2. 옵션 선택 → `onSelect(value)` 콜백 호출 (호스트가 `selected` state 갱신)
 *   3. "추가하기" 클릭 → `onConfirm()` 콜백 호출 (호스트가 다음 시트(이름 입력) 진입
 *      또는 즉시 추가 처리를 결정)
 */
export function AddRoomTypeSheet<T extends string>({
  open,
  options,
  selected,
  isSubmitting = false,
  ctaLabel = '추가하기',
  ctaSubmittingLabel = '추가 중...',
  title = '어떤 방을 추가하시겠어요?',
  onSelect,
  onConfirm,
  onClose,
  container,
}: AddRoomTypeSheetProps<T>) {
  return (
    <BottomSheet
      open={open}
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      container={container}
      aria-label="방 타입 선택"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: '#111827',
            letterSpacing: '-0.025em',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {options.map((opt, i) => {
            const isSelected = selected === opt.value;
            const isLast = i === options.length - 1;
            return (
              <Box
                key={opt.value}
                component="button"
                onClick={() => onSelect(opt.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: '15px',
                  background: 'none',
                  border: 'none',
                  borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  fontSize: 16,
                  color: '#111827',
                  fontWeight: isSelected ? 600 : 400,
                  letterSpacing: '-0.02em',
                }}
              >
                {opt.label}
                {isSelected && (
                  <Box sx={{ color: '#055cf5', fontSize: 16, fontWeight: 700 }}>✓</Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Button
          variant="primary"
          size="l"
          width="fixed"
          onPress={onConfirm}
          disabled={!selected || isSubmitting}
        >
          {isSubmitting ? ctaSubmittingLabel : ctaLabel}
        </Button>
      </Box>
    </BottomSheet>
  );
}

export default AddRoomTypeSheet;
